"use client";
import { useAuth } from "@/hook/useAuth";
import {
	addFavoritesCards,
	getFavoritesCards,
	removeFavoritesCards,
} from "@/store/feature/ai-course-finder/aiCourseApi";
import { setCurrentDetail } from "@/store/feature/ai-course-finder/aiCourseFinderSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { UniversityItem } from "@/store/types/universityTypes";
import { checkCountry, replacedName } from "@/utils/Constants";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Check, Plus } from "lucide-react";
import { getFormattedCountryName } from "@/utils/getFormattedCountryName";
import Link from "next/link";
import ReactDOM from "react-dom";
import { ImageLogo } from "@/components/common/ImageLogo";
import { CardFavorite } from "@/components/common/Icons";

interface NewUniversitiesCardsProps {
	items: UniversityItem;
	buttonText?: string;
	showLimitedFields?: boolean;
	detailsButtonText?: string;
	comparisonData?: UniversityItem[];
	fromSearch?: boolean;
	compare?: boolean;
	setComparisonData?: React.Dispatch<React.SetStateAction<UniversityItem[]>>;
	setRegistrationLoginPopup?: React.Dispatch<React.SetStateAction<boolean>>;
	setEmailVerificationPopup?: React.Dispatch<React.SetStateAction<boolean>>;
	registrationLoginPopup?: boolean;
	exploreHanlder?: (collegeId: string) => void;
	emailVerificationPopUp?: boolean;
}

const NewUniversityCard = ({
	items,
	detailsButtonText,
	showLimitedFields,
	setComparisonData,
	buttonText,
	exploreHanlder,
	fromSearch,
	compare,
	comparisonData = [],
	setRegistrationLoginPopup,
}: NewUniversitiesCardsProps) => {
	const dispatch = useAppDispatch();
	const router = useRouter();
	const { user, isAnonymous } = useAuth();
	const [loading, setLoading] = useState(false);
	const [favoritePopUp, setFavoritePopUp] = useState(false);
	const [isChecked, setIsChecked] = useState(false);
	const [isNavigating, setIsNavigating] = useState(false);

	const { favoritesCardList } = useAppSelector((state) => state.aiCourseFinder);

	const favoritesCardId = Array.isArray(favoritesCardList?.colleges)
		? favoritesCardList.colleges.map((item) => item.college_id)
		: [];

	useEffect(() => {
		if (
			comparisonData &&
			comparisonData.some((item) => item.collegeId === items.collegeId)
		) {
			setIsChecked(true);
		} else {
			setIsChecked(false);
		}
	}, [comparisonData, items.collegeId]);

	const handleComparisonChange = () => {
		setIsChecked(!isChecked);
		if (setComparisonData) {
			setComparisonData((prev) => {
				if (!prev) return [];
				if (!isChecked) {
					if (prev.length >= 10) return prev;
					return [...prev, items];
				} else {
					return prev.filter((item) => item.collegeId !== items.collegeId);
				}
			});
		}
	};

	const exploreCourseHandler = async () => {
		await dispatch(
			setCurrentDetail({
				countryId: items.countryId,
				collegeId: items.collegeId,
				collegeName: items.title,
				countryName: items.country,
			}),
		);
		if (buttonText) {
			if (items.collegeId) {
				await exploreHanlder?.(String(items.collegeId));
			}
			router.push("/ai-course-finder/global-search-course");
		} else {
			router.push("/explore-courses");
		}
	};

	const addFavorites = async () => {
		if (!isAnonymous) {
			try {
				setLoading(true);
				const payload = {
					favouriteId: items.collegeId || "",
					studentId: user?.uid || "",
					type: "college",
				};
				await dispatch(addFavoritesCards(payload));
				await dispatch(getFavoritesCards(payload.studentId));
				setLoading(false);
			} catch (error) {
				console.error("error", error);
				setLoading(false);
			}
		} else {
			setRegistrationLoginPopup?.(true);
			setLoading(false);
		}
	};

	const confirmRemove = async () => {
		setFavoritePopUp(true);
	};

	const removeFavorites = async () => {
		if (loading) return;
		setLoading(true);
		const payload = {
			favouriteId: items.collegeId || "",
			userId: user?.uid || "",
		};
		await dispatch(removeFavoritesCards(payload));
		await dispatch(getFavoritesCards(payload.userId));
		setFavoritePopUp(false);
		setLoading(false);
	};

	const handleRedirect = () => {
		if (isNavigating) return;
		setIsNavigating(true);
		router.push(`/${items.collegeSlug}`);
	};
	console.log(items.qsRanking, "qsranking")
	return (
		<div className="w-full h-full rounded-[24px] overflow-hidden shadow-lg shadow-[0px_2px_8px_0px_#0000001F]">
			<div className="bg-white rounded-[24px] h-full transition-all duration-300 flex flex-col border border-gray-100">

				{/* Header Section */}
				<div className="relative p-4 sm:p-[17px] bg-linear-to-r from-[#1776F0] to-[#8F3EBC] flex gap-3 sm:gap-4 min-h-[130px] sm:min-h-[140px] items-start rounded-t-xl">
					<div
						onClick={handleRedirect}
						className="rounded-[10px] overflow-hidden min-w-[60px] sm:min-w-[70px] max-w-[60px] sm:max-w-[70px] h-[60px] sm:h-[70px] bg-white flex items-center justify-center shadow-sm cursor-pointer"
					>
						<div className="w-full h-full p-1 flex items-center justify-center">
							<ImageLogo
								college_name={items.title ?? ""}
								country={items.country ?? ""}
							/>
						</div>
					</div>
					<div className="w-full pr-8">
						<p className="ff_poppins font-bold text-sm sm:text-base md:text-lg text-white mb-1 leading-tight line-clamp-2">
							{items.title}
						</p>
						<p className="ff_poppins font-medium text-xs sm:text-sm text-white/90 mb-1 leading-tight line-clamp-1">
							{getFormattedCountryName(
								items.country ?? "",
								checkCountry,
								replacedName,
							)}
						</p>

						<div className="flex items-center justify-end mt-[12px] absolute top-4 right-4">
							<span
								onClick={() => {
									if (!loading) {
										if (!!items.collegeId && favoritesCardId.includes(items.collegeId)) {
											confirmRemove();
										} else {
											addFavorites();
										}
									}
								}}
								className={`size-[32px] inline-flex bg-white/20 backdrop-blur-sm rounded-full items-center justify-center ${loading ? "cursor-disabled opacity-50" : "cursor-pointer hover:bg-white/30 transition"
									}`}
							>
								<CardFavorite
									isFavorite={!!items.collegeId && favoritesCardId.includes(items.collegeId)}
								/>
							</span>
						</div>
					</div>

					{items.popularity && (
						<div className="absolute bottom-4 right-4 bg-white rounded-full px-3 py-1 flex items-center shadow-sm">
							<p className="text-[#3777FF] font-bold ff_poppins text-[11px]">
								{items.popularity}
							</p>
						</div>
					)}
				</div>

				{/* Content Section (Details Grid) */}
				<div className="p-4 sm:p-5 flex flex-col gap-4 bg-white flex-1">
					<div className="grid grid-cols-2 gap-3">
						{items.category && (
							<div className="rounded-[8px] p-[10px] bg-[#D4E6FF]">
								<p className="ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary">
									Category
								</p>
								<p className="text-xs lg:text-sm font-semibold ff_poppins text-center text-black! capitalize">
									{items.category}
								</p>
							</div>
						)}
						{((items as any).qsRanking || (items as any)["QS Ranking"]) &&
							((items as any).qsRankingYear || (items as any)["QS Ranking Year"]) && (
							<div className="rounded-[8px] p-[10px] bg-[#D4E6FF]">
								<p className="ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary">
									QS Ranking
								</p>
								<p className="text-xs lg:text-sm font-semibold ff_poppins text-center text-black!">
									{(items as any).qsRanking || (items as any)["QS Ranking"]}
								</p>
							</div>
						)}
						{(items.offerTat || (items as any).offer_tat) && (
							<div className="rounded-[8px] p-[10px] bg-[#D4E6FF]">
								<p className="ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary">
									Offer TAT
								</p>
								<p className="text-xs lg:text-sm font-semibold ff_poppins text-center text-black!">
									{items.offerTat || (items as any).offer_tat} {String(items.offerTat || (items as any).offer_tat).toLowerCase().includes("day") ? "" : "Days"}
								</p>
							</div>
						)}
						<div className="rounded-[8px] p-[10px] bg-[#D4E6FF]">
							<p className="ff_poppins text-[10px] lg:text-xs font-medium text-center text-primary">
								Open Intake
							</p>
							<p className="text-xs lg:text-sm font-semibold ff_poppins text-center text-black! capitalize">
								{items.intake || "Sept 2027"}
							</p>
						</div>
					</div>

					<div className="grid grid-cols-2 gap-y-4 sm:gap-y-5 gap-x-3 sm:gap-x-4">
						{/* <div className="flex flex-col">
							<p className="ff_poppins text-[#A0A0A0] text-[10px] tracking-wider uppercase font-semibold mb-1">
								ACCEPTANCE RATE
							</p>
							<p className="ff_poppins text-[#333] font-bold text-sm">
								{(items as any).acceptanceRate || "65%"}
							</p>
						</div> */}
						{/* <div className="flex flex-col">
							<p className="ff_poppins text-[#A0A0A0] text-[10px] tracking-wider uppercase font-semibold mb-1">
								Application Fees
							</p>
							<p className="ff_poppins text-[#333] font-bold text-sm">
								{Number(items.applicationFees) === 0 ? (
									<span>No Application Fee</span>
								) : items.currency === "GBP" && Number(items.applicationFees) > 0 ? (
									<span className="relative before:absolute before:w-full before:h-[2px] before:bg-red-600 before:top-1/2 before:-translate-y-1/2 before:left-0 before:rotate-[3deg]">
										{`${items.currency} ${String(items.applicationFees).replace(".00", "")}`}
										<span className="absolute text-pink-500 top-full left-0 text-nowrap text-xs">
											Fees Waived Off
										</span>
									</span>
								) : Number(items.applicationFees) > 0 ? (
									`${items.currency || ""} ${String(items.applicationFees).replace(".00", "")}`
								) : (
									"Not Announced"
								)}
							</p>
						</div> */}
					</div>
				</div>

				{items.specialisationCategory && items.specialisationCategory.length > 0 && (
					<div className="flex flex-wrap gap-1.5 px-4 sm:px-5 pb-3">
						{items.specialisationCategory.slice(0, 3).map((cat, i) => (
							<span key={i} className="bg-[#E5F0FF] text-[#3776FF] text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ff_poppins">
								{cat}
							</span>
						))}
					</div>
				)}

				{/* Footer Actions Section */}
				{!showLimitedFields && (
					<div className="mt-auto">
						{compare && (
							<div className="relative border-t border-gray-100 flex items-center justify-center pt-3 pb-3">
								<div
									onClick={() => {
										if (isChecked || comparisonData.length < 10) {
											handleComparisonChange();
										}
									}}
									className={`flex items-center gap-1.5 px-3 bg-white relative z-10 -mt-[25px] cursor-pointer text-xs font-semibold ff_poppins ${isChecked
										? "text-[#2F7D5B]"
										: !isChecked && comparisonData.length >= 10
											? "opacity-50 cursor-not-allowed text-gray-400"
											: "text-gray-500 hover:text-gray-700"
										}`}
								>
									{!isChecked ? <Plus className="size-3.5" /> : <Check className="size-3.5" />}
									<span>{!isChecked ? "Add to compare" : "Shortlisted"}</span>
								</div>
							</div>
						)}

						<div className="px-4 sm:px-5 pb-4 sm:pb-5 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-3 sm:mt-4">
							<Link
								href={`/${items.collegeSlug}`}
								className="flex items-center justify-center w-full py-2.5 rounded-lg border border-gray-300 text-[#333] font-semibold ff_poppins text-sm hover:border-[#3776FF] hover:text-[#3776FF] hover:bg-blue-50 transition-colors shadow-sm"
							>
								{detailsButtonText || "View Details"}
							</Link>
							<button
								onClick={() =>
									fromSearch
										? router.push(`/all-courses/${items.collegeSlug}`)
										: exploreCourseHandler()
								}
								className="w-full py-2.5 rounded-lg bg-gradient-to-r from-[#2962FF] to-[#8A2BE2] hover:opacity-90 text-white font-semibold ff_poppins text-sm transition-all text-center shadow-sm"
							>
								{buttonText
									? buttonText
									: fromSearch && items?.courseCount
										? `Explore ${items?.courseCount ?? ""} Courses`
										: fromSearch
											? "Explore Courses"
											: `Explore ${items?.courseCount ?? ""} Courses`}
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Favorite Remove Popup */}
			{favoritePopUp &&
				typeof window !== "undefined" &&
				ReactDOM.createPortal(
					<div
						className="fixed bottom-0 left-0 w-full h-full z-[999] flex items-end justify-center"
						onClick={() => {
							if (!loading) setFavoritePopUp(false);
						}}
					>
						<span className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer" />
						<div
							className="bg-[#0E1116] text-white relative z-20 max-w-md w-full rounded-t-[32px] text-center shadow-[0px_-4px_12px_0px_#ffffff40] overflow-hidden"
							onClick={(e) => e.stopPropagation()}
						>
							<p className="text-base sm:text-lg md:text-xl font-semibold bg-white/10 py-6 px-4">
								<span className="max-w-[408px] inline-block">
									Are you sure you want to remove this from favourites?
								</span>
							</p>
							<div className="grid grid-cols-2 gap-3 mt-5 pb-8 px-4 sm:px-6">
								<button
									type="button"
									onClick={() => removeFavorites()}
									disabled={loading}
									className="text-white py-3 sm:py-4 px-4 rounded-full border border-[#0162E8] font-medium text-base sm:text-[18px] cursor-pointer bg-[#0162E8] disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{loading ? "Removing..." : "Yes"}
								</button>
								<button
									type="button"
									onClick={() => setFavoritePopUp(false)}
									disabled={loading}
									className="text-white py-3 sm:py-4 px-4 rounded-full border border-white/20 text-base sm:text-[18px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
								>
									No
								</button>
							</div>
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
};

export default NewUniversityCard;
